import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withModifiers as _withModifiers, withKeys as _withKeys, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-red-500"
}
const _hoisted_2 = ["id", "type", "value", "placeholder", "disabled", "readonly", "min", "max", "step", "tabindex"]
const _hoisted_3 = {
  key: 1,
  class: "relative"
}
const _hoisted_4 = ["id", "disabled", "tabindex", "aria-expanded", "onKeydown"]
const _hoisted_5 = {
  key: 0,
  class: "absolute left-0 right-0 top-full z-[300] mt-1 max-h-60 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg",
  role: "listbox"
}
const _hoisted_6 = ["aria-selected", "onMouseenter", "onClick"]
const _hoisted_7 = ["id", "value", "disabled", "tabindex"]
const _hoisted_8 = {
  key: 0,
  value: ""
}
const _hoisted_9 = ["value"]
const _hoisted_10 = ["id", "value", "placeholder", "disabled", "readonly", "rows", "tabindex", "maxlength"]
const _hoisted_11 = {
  key: 4,
  class: "flex items-center"
}
const _hoisted_12 = ["id", "checked", "disabled", "tabindex"]
const _hoisted_13 = ["for"]
const _hoisted_14 = {
  key: 5,
  class: "space-y-1.5"
}
const _hoisted_15 = ["id", "value", "checked", "disabled", "tabindex", "onChange"]
const _hoisted_16 = ["for"]
const _hoisted_17 = ["id", "value", "tabindex"]
const _hoisted_18 = ["id", "accept", "multiple", "tabindex"]
const _hoisted_19 = {
  key: 8,
  class: "absolute left-0 top-full z-[200] mt-0.5 text-xs text-red-600 bg-white border border-red-200 rounded px-1.5 py-0.5 shadow-md whitespace-nowrap pointer-events-none",
  role: "alert"
}
const _hoisted_20 = {
  key: 0,
  class: "text-green-600"
}
const _hoisted_21 = {
  key: 1,
  class: "text-red-500"
}
const _hoisted_22 = {
  key: 2,
  class: "mt-1 text-xs text-black"
}

import { computed, nextTick, ref, watch } from 'vue'
import type { BaseInputProps } from '@/types/ui';
import { focusRelativeElement } from '@/utils/focusNavigation'


export default /*@__PURE__*/_defineComponent({
  __name: 'BaseInput',
  props: {
    modelValue: { type: [String, Number, Boolean, null] },
    label: {},
    labelClass: {},
    type: { default: 'text' },
    placeholder: {},
    options: {},
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    error: {},
    forceValidate: { type: Boolean, default: false },
    validateOnBlur: { type: Boolean, default: true },
    openOnFocus: { type: Boolean, default: false },
    skipTab: { type: Boolean, default: false },
    helpText: {},
    id: {},
    min: {},
    max: {},
    step: {},
    rows: { default: 3 },
    accept: {},
    multiple: { type: Boolean, default: false },
    customClass: {},
    minLength: {},
    maxLength: {}
  },
  emits: ["update:modelValue", "change", "validation-changed"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const uniqueId = computed(() => props.id || `input-${Math.random().toString(36).substr(2, 9)}`)
const touched = ref(false)
const customSelectButtonRef = ref<HTMLButtonElement | null>(null)
const customSelectOpen = ref(false)
const customSelectHighlightedIndex = ref(-1)
const customSelectPointerDown = ref(false)

const selectOptions = computed(() => props.options || [])
const selectedOptionLabel = computed(
  () =>
    selectOptions.value.find(
      (option) => String(option.value) === String(props.modelValue ?? ''),
    )?.label || '',
)

const showFieldErrorOverlay = computed(
  () => !['checkbox', 'radio'].includes(props.type),
)

const isEmptyValue = computed(() => {
  const v = props.modelValue
  if (v === null || v === undefined) return true
  if (typeof v === 'string') return v.trim() === ''
  if (typeof v === 'number') return Number.isNaN(v)
  if (typeof v === 'boolean') return false
  return false
})

const hasRequiredError = computed(
  () =>
    props.required &&
    isEmptyValue.value &&
    (touched.value || props.forceValidate),
)

const showErrorBorder = computed(() => !!props.error || hasRequiredError.value)

const displayErrorMessage = computed(() => {
  if (props.error) return props.error
  if (hasRequiredError.value) return 'Ce champ est obligatoire'
  return ''
})

const invalidInputClass = computed(() =>
  showErrorBorder.value
    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
    : 'border-gray-300',
)

const onBlur = () => {
  if (props.validateOnBlur && props.required) {
    touched.value = true
  }
}

const openCustomSelect = () => {
  if (props.disabled) return
  const selectedIndex = selectOptions.value.findIndex(
    (option) => String(option.value) === String(props.modelValue ?? ''),
  )
  customSelectHighlightedIndex.value = selectedIndex
  customSelectOpen.value = true
}

const handleCustomSelectFocus = () => {
  if (!customSelectPointerDown.value) openCustomSelect()
}

const handleCustomSelectBlur = () => {
  customSelectPointerDown.value = false
  customSelectOpen.value = false
  onBlur()
}

const handleCustomSelectClick = () => {
  customSelectOpen.value = !customSelectOpen.value
  if (customSelectOpen.value) openCustomSelect()
  customSelectPointerDown.value = false
}

const moveCustomSelectHighlight = (delta: number) => {
  if (!customSelectOpen.value) openCustomSelect()
  const length = selectOptions.value.length
  if (!length) return
  if (customSelectHighlightedIndex.value < 0) {
    customSelectHighlightedIndex.value = delta > 0 ? 0 : length - 1
  } else {
    customSelectHighlightedIndex.value =
      (customSelectHighlightedIndex.value + delta + length) % length
  }
}

const selectCustomOption = (value: string) => {
  emit('update:modelValue', value)
  emit('change', value)
  customSelectOpen.value = false
}

const confirmCustomSelectHighlight = () => {
  if (!customSelectOpen.value) {
    openCustomSelect()
    return
  }
  const option =
    selectOptions.value[
      customSelectHighlightedIndex.value >= 0
        ? customSelectHighlightedIndex.value
        : 0
    ]
  if (option) selectCustomOption(option.value)
}

const handleCustomSelectTab = (event: KeyboardEvent) => {
  customSelectOpen.value = false
  void nextTick(() => {
    focusRelativeElement(customSelectButtonRef.value, event.shiftKey ? -1 : 1)
  })
}

const selectNativeOption = (event: Event) => {
  const value = (event.target as HTMLSelectElement).value
  emit('update:modelValue', value)
  emit('change', value)
}

watch(
  () => props.modelValue,
  () => {
    if (!isEmptyValue.value && touched.value) {
      // garde touched mais l'erreur disparaît via isEmptyValue
    }
  },
)

watch(
  () => props.forceValidate,
  (forced) => {
    if (forced && props.required) touched.value = true
  },
)

const handleTextareaInput = (event: Event) => {
  const value = (event.target as HTMLTextAreaElement).value
  emit('update:modelValue', value)

  if (props.minLength) {
    const isValid = value.length === 0 || value.length >= props.minLength
    emit('validation-changed', isValid)
  }
}

const handleWheel = (event: Event) => {
  if (props.type !== 'number') return
  ;(event.target as HTMLInputElement).blur()
}

const getCharCountClass = () => {
  const length = String(props.modelValue || '').length
  if (length >= (props.minLength || 0)) {
    return 'text-green-600'
  } else if (length > 0) {
    return 'text-red-500'
  }
  return 'text-black'
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (_ctx.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          class: _normalizeClass(["block text-sm text-black mb-1", _ctx.labelClass || 'font-medium'])
        }, [
          _createTextVNode(_toDisplayString(_ctx.label) + " ", 1),
          (_ctx.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_1, "*"))
            : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass({ relative: showFieldErrorOverlay.value })
    }, [
      (['text', 'number', 'email', 'tel', 'date', 'datetime-local', 'password'].includes(_ctx.type))
        ? (_openBlock(), _createElementBlock("input", {
            key: 0,
            id: uniqueId.value,
            type: _ctx.type,
            value: _ctx.modelValue,
            placeholder: _ctx.placeholder,
            disabled: _ctx.disabled,
            readonly: _ctx.readonly,
            min: _ctx.min,
            max: _ctx.max,
            step: _ctx.step,
            tabindex: _ctx.skipTab ? -1 : undefined,
            onInput: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('update:modelValue', ($event.target as HTMLInputElement).value))),
            onBlur: onBlur,
            onWheel: handleWheel,
            class: _normalizeClass([

          'w-full h-9 px-3 text-sm text-black placeholder-gray-400 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors',
          _ctx.disabled ? 'bg-white cursor-not-allowed' : 'bg-white',
          _ctx.readonly ? 'bg-gray-50' : '',
          invalidInputClass.value,
          _ctx.customClass
        ])
          }, null, 42, _hoisted_2))
        : (_ctx.type === 'select' && _ctx.openOnFocus)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("button", {
                id: uniqueId.value,
                ref_key: "customSelectButtonRef",
                ref: customSelectButtonRef,
                type: "button",
                disabled: _ctx.disabled,
                tabindex: _ctx.skipTab ? -1 : undefined,
                class: _normalizeClass(["w-full h-9 px-3 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors flex items-center justify-between gap-2 text-left", [
            invalidInputClass.value,
            _ctx.disabled ? 'cursor-not-allowed opacity-60' : '',
            _ctx.customClass,
          ]]),
                "aria-expanded": customSelectOpen.value,
                "aria-haspopup": "listbox",
                onPointerdown: _cache[1] || (_cache[1] = ($event: any) => (customSelectPointerDown.value = true)),
                onFocus: handleCustomSelectFocus,
                onBlur: handleCustomSelectBlur,
                onClick: handleCustomSelectClick,
                onKeydown: [
                  _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (moveCustomSelectHighlight(1)), ["prevent"]), ["down"])),
                  _cache[3] || (_cache[3] = _withKeys(_withModifiers(($event: any) => (moveCustomSelectHighlight(-1)), ["prevent"]), ["up"])),
                  _withKeys(_withModifiers(confirmCustomSelectHighlight, ["prevent"]), ["enter"]),
                  _withKeys(_withModifiers(confirmCustomSelectHighlight, ["prevent"]), ["space"]),
                  _cache[4] || (_cache[4] = _withKeys(_withModifiers(($event: any) => (customSelectOpen.value = false), ["prevent"]), ["esc"])),
                  _withKeys(_withModifiers(handleCustomSelectTab, ["prevent"]), ["tab"])
                ]
              }, [
                _createElementVNode("span", {
                  class: _normalizeClass(["truncate", selectedOptionLabel.value ? 'text-black' : 'text-gray-400'])
                }, _toDisplayString(selectedOptionLabel.value || _ctx.placeholder || "Sélectionner"), 3),
                _createElementVNode("i", {
                  class: _normalizeClass(["fas fa-chevron-down text-xs text-gray-400 transition-transform", customSelectOpen.value ? 'rotate-180' : ''])
                }, null, 2)
              ], 42, _hoisted_4),
              (customSelectOpen.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    (_ctx.placeholder)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          type: "button",
                          tabindex: "-1",
                          class: _normalizeClass(["w-full px-3 py-2 text-left text-sm text-gray-500", customSelectHighlightedIndex.value === -1 ? 'bg-blue-100' : 'hover:bg-gray-100']),
                          onMousedown: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["prevent"])),
                          onClick: _cache[6] || (_cache[6] = ($event: any) => (selectCustomOption('')))
                        }, _toDisplayString(_ctx.placeholder), 35))
                      : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.options, (option, index) => {
                      return (_openBlock(), _createElementBlock("button", {
                        key: `option-${option.value}`,
                        type: "button",
                        tabindex: "-1",
                        class: _normalizeClass(["w-full px-3 py-2 text-left text-sm text-black", [
              index === customSelectHighlightedIndex.value
                ? 'bg-blue-100'
                : 'hover:bg-gray-100',
              String(option.value) === String(_ctx.modelValue) ? 'font-semibold' : '',
            ]]),
                        role: "option",
                        "aria-selected": String(option.value) === String(_ctx.modelValue),
                        onMousedown: _cache[7] || (_cache[7] = _withModifiers(() => {}, ["prevent"])),
                        onMouseenter: ($event: any) => (customSelectHighlightedIndex.value = index),
                        onClick: ($event: any) => (selectCustomOption(option.value))
                      }, _toDisplayString(option.label), 43, _hoisted_6))
                    }), 128))
                  ]))
                : _createCommentVNode("", true)
            ]))
          : (_ctx.type === 'select')
            ? (_openBlock(), _createElementBlock("select", {
                key: 2,
                id: uniqueId.value,
                value: _ctx.modelValue,
                disabled: _ctx.disabled,
                tabindex: _ctx.skipTab ? -1 : undefined,
                onChange: selectNativeOption,
                onBlur: onBlur,
                class: _normalizeClass([
          'w-full h-9 px-3 text-sm text-black border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors',
          invalidInputClass.value,
          _ctx.customClass
        ])
              }, [
                (_ctx.placeholder)
                  ? (_openBlock(), _createElementBlock("option", _hoisted_8, _toDisplayString(_ctx.placeholder), 1))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.options, (option) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: `option-${option.value}`,
                    value: option.value
                  }, _toDisplayString(option.label), 9, _hoisted_9))
                }), 128))
              ], 42, _hoisted_7))
            : (_ctx.type === 'textarea')
              ? (_openBlock(), _createElementBlock("textarea", {
                  key: 3,
                  id: uniqueId.value,
                  value: String(_ctx.modelValue || ''),
                  placeholder: _ctx.placeholder,
                  disabled: _ctx.disabled,
                  readonly: _ctx.readonly,
                  rows: _ctx.rows,
                  tabindex: _ctx.skipTab ? -1 : undefined,
                  onInput: handleTextareaInput,
                  onBlur: onBlur,
                  class: _normalizeClass([
          'w-full px-3 py-2 text-sm text-black placeholder-gray-400 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none',
          _ctx.disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white',
          _ctx.readonly ? 'bg-gray-50' : '',
          invalidInputClass.value,
          _ctx.customClass
        ]),
                  maxlength: props.maxLength
                }, null, 42, _hoisted_10))
              : (_ctx.type === 'checkbox')
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createElementVNode("input", {
                      id: uniqueId.value,
                      type: "checkbox",
                      checked: Boolean(_ctx.modelValue),
                      disabled: _ctx.disabled,
                      tabindex: _ctx.skipTab ? -1 : undefined,
                      onChange: _cache[8] || (_cache[8] = ($event: any) => (_ctx.$emit('update:modelValue', ($event.target as HTMLInputElement).checked))),
                      class: _normalizeClass([
            'w-4 h-4 rounded focus:ring-1 focus:ring-blue-500',
            _ctx.error ? 'border-red-300 text-red-600' : 'border-gray-300 text-blue-600'
          ])
                    }, null, 42, _hoisted_12),
                    _createElementVNode("label", {
                      for: uniqueId.value,
                      class: "ml-2 text-sm text-black cursor-pointer"
                    }, _toDisplayString(_ctx.placeholder), 9, _hoisted_13)
                  ]))
                : (_ctx.type === 'radio')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.options, (option) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: option.value,
                          class: "flex items-center"
                        }, [
                          _createElementVNode("input", {
                            id: `${uniqueId.value}-${option.value}`,
                            type: "radio",
                            value: option.value,
                            checked: _ctx.modelValue === option.value,
                            disabled: _ctx.disabled,
                            tabindex: _ctx.skipTab ? -1 : undefined,
                            onChange: ($event: any) => (_ctx.$emit('update:modelValue', option.value)),
                            class: _normalizeClass([
              'w-4 h-4 focus:ring-1 focus:ring-blue-500',
              _ctx.error ? 'border-red-300 text-red-600' : 'border-gray-300 text-blue-600'
            ])
                          }, null, 42, _hoisted_15),
                          _createElementVNode("label", {
                            for: `${uniqueId.value}-${option.value}`,
                            class: "ml-2 text-sm text-black cursor-pointer"
                          }, _toDisplayString(option.label), 9, _hoisted_16)
                        ]))
                      }), 128))
                    ]))
                  : (_ctx.type === 'color')
                    ? (_openBlock(), _createElementBlock("input", {
                        key: 6,
                        id: uniqueId.value,
                        type: "color",
                        value: _ctx.modelValue,
                        tabindex: _ctx.skipTab ? -1 : undefined,
                        onInput: _cache[9] || (_cache[9] = ($event: any) => (_ctx.$emit('update:modelValue', ($event.target as HTMLInputElement).value))),
                        class: "w-full h-10 border border-gray-300 rounded-md cursor-pointer"
                      }, null, 40, _hoisted_17))
                    : (_ctx.type === 'file')
                      ? (_openBlock(), _createElementBlock("input", {
                          key: 7,
                          id: uniqueId.value,
                          type: "file",
                          accept: _ctx.accept,
                          multiple: _ctx.multiple,
                          tabindex: _ctx.skipTab ? -1 : undefined,
                          onChange: _cache[10] || (_cache[10] = ($event: any) => (_ctx.$emit('update:modelValue', ($event.target as HTMLInputElement).files))),
                          class: "block w-full text-xs text-black file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        }, null, 40, _hoisted_18))
                      : _createCommentVNode("", true),
      (displayErrorMessage.value && showFieldErrorOverlay.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_19, _toDisplayString(displayErrorMessage.value), 1))
        : _createCommentVNode("", true)
    ], 2),
    (_ctx.type === 'textarea' && _ctx.minLength)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(["mt-1 text-xs", getCharCountClass()])
        }, [
          _createTextVNode(_toDisplayString(String(_ctx.modelValue || '').length) + "/" + _toDisplayString(_ctx.minLength) + " caractères ", 1),
          (String(_ctx.modelValue || '').length >= _ctx.minLength)
            ? (_openBlock(), _createElementBlock("span", _hoisted_20, "✅"))
            : (String(_ctx.modelValue || '').length > 0)
              ? (_openBlock(), _createElementBlock("span", _hoisted_21, "❌"))
              : _createCommentVNode("", true)
        ], 2))
      : _createCommentVNode("", true),
    (_ctx.helpText)
      ? (_openBlock(), _createElementBlock("p", _hoisted_22, _toDisplayString(_ctx.helpText), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})